// evr-guard-utils.js
// Shared utilities for EVR in-theme guard/bridge scripts.
// No bundler needed: exposes window.EVR.GuardUtils.

(function () {
  window.EVR = window.EVR || {};
  if (window.EVR.GuardUtils) return; // avoid double-init

  const GuardUtils = {};

  // -----------------------
  //  URL helpers
  // -----------------------

  GuardUtils.safeUrl = function () {
    try {
      return new URL(window.location.href);
    } catch (_) {
      return null;
    }
  };

  GuardUtils.getParam = function (key) {
    const url = GuardUtils.safeUrl();
    return url ? url.searchParams.get(key) : null;
  };

  GuardUtils.getCouponFromUrl = function () {
    return GuardUtils.getParam("coupon") || GuardUtils.getParam("discount");
  };

  GuardUtils.getClusterIdFromUrl = function () {
    return GuardUtils.getParam("clusterId") || GuardUtils.getParam("cluster_id") || null;
  };

  GuardUtils.cleanUrl = function (keysToRemove) {
    try {
      const url = new URL(window.location.href);
      let changed = false;

      (keysToRemove || []).forEach((k) => {
        if (url.searchParams.has(k)) {
          url.searchParams.delete(k);
          changed = true;
        }
      });

      if (changed) window.history.replaceState({}, "", url.toString());
    } catch (_) {}
  };

  // -----------------------
  //  ENV
  // -----------------------

  GuardUtils.resolveEnvFromUrlAndStorage = function () {
    const url = GuardUtils.safeUrl();
    const queryEnv = (url && url.searchParams.get("env") ? url.searchParams.get("env") : "").toLowerCase();
    const storedEnv = (localStorage.getItem("env") || "prod").toLowerCase();
    const env = (queryEnv || storedEnv || "prod").toLowerCase();

    if (env !== storedEnv) {
      localStorage.setItem("env", env);
      try {
        sessionStorage.removeItem("evr_jwt_login_done");
      } catch (_) {}
      // sign out to avoid cross-env leakage
      GuardUtils.signOutCurrentAuthIfPossible(env).catch(() => {});
    } else {
      localStorage.setItem("env", env);
    }

    return env;
  };

  // -----------------------
  //  Firebase auth helpers
  // -----------------------

  GuardUtils.getAuthForEnvSafe = function (env) {
    if (window.EVR && typeof window.EVR.getAuthForEnv === "function") {
      return window.EVR.getAuthForEnv(env);
    }
    if (window.firebase && typeof window.firebase.auth === "function") {
      return window.firebase.auth();
    }
    return null;
  };

  GuardUtils.signOutAuth = function (auth) {
    if (auth && typeof auth.signOut === "function") return auth.signOut();
    if (window.firebase && window.firebase.auth) return window.firebase.auth().signOut();
    return Promise.resolve();
  };

  GuardUtils.signInWithCustomTokenCompat = function (auth, token) {
    if (auth && typeof auth.signInWithCustomToken === "function") return auth.signInWithCustomToken(token);
    if (window.firebase && window.firebase.auth) return window.firebase.auth().signInWithCustomToken(token);
    return Promise.reject(new Error("No signInWithCustomToken available on auth instance"));
  };

  GuardUtils.signOutCurrentAuthIfPossible = async function (env) {
    const auth = GuardUtils.getAuthForEnvSafe(env);
    if (!auth) return;
    try {
      await GuardUtils.signOutAuth(auth);
    } catch (_) {}
  };

  // auto-login reads token from URL and signs in; env passed explicitly
  GuardUtils.autoLoginFromUrlIfPresent = async function (env) {
    const url = GuardUtils.safeUrl();
    if (!url) return;

    const jwt = url.searchParams.get("jwt") || url.searchParams.get("customToken");
    if (!jwt) return;

    const auth = GuardUtils.getAuthForEnvSafe(env);
    if (!auth) {
      console.error("[EVR.GuardUtils] No Firebase auth instance available for env:", env);
      return;
    }

    try {
      await GuardUtils.signOutAuth(auth);
    } catch (_) {}

    await GuardUtils.signInWithCustomTokenCompat(auth, jwt);

    try {
      sessionStorage.setItem("evr_jwt_login_done", "true");
    } catch (_) {}

    GuardUtils.cleanUrl(["jwt", "customToken"]);
  };

  GuardUtils.ensureFirebaseUser = function (env, timeoutMs) {
    timeoutMs = timeoutMs || 4000;

    return new Promise((resolve) => {
      const auth = GuardUtils.getAuthForEnvSafe(env);
      if (!auth) {
        console.error("[EVR.GuardUtils] ensureFirebaseUser: no auth instance for env", env);
        resolve(null);
        return;
      }

      if (auth.currentUser) {
        resolve(auth.currentUser);
        return;
      }

      let resolved = false;
      let unsubscribe = null;

      try {
        if (typeof auth.onAuthStateChanged === "function") {
          unsubscribe = auth.onAuthStateChanged(function (user) {
            if (resolved) return;
            resolved = true;
            try {
              if (typeof unsubscribe === "function") unsubscribe();
            } catch (_) {}
            resolve(user || null);
          });
        } else if (window.firebase && window.firebase.auth) {
          const compatAuth = window.firebase.auth();
          unsubscribe = compatAuth.onAuthStateChanged(function (user) {
            if (resolved) return;
            resolved = true;
            try {
              if (typeof unsubscribe === "function") unsubscribe();
            } catch (_) {}
            resolve(user || null);
          });
        }
      } catch (e) {
        console.error("[EVR.GuardUtils] onAuthStateChanged setup failed:", e);
      }

      setTimeout(function () {
        if (resolved) return;
        resolved = true;

        try {
          if (typeof unsubscribe === "function") unsubscribe();
        } catch (_) {}

        const current =
          auth.currentUser ||
          (window.firebase && window.firebase.auth && window.firebase.auth().currentUser) ||
          null;
        resolve(current);
      }, timeoutMs);
    });
  };

  GuardUtils.getFirebaseIdTokenOrNull = async function (user) {
    try {
      if (user && typeof user.getIdToken === "function") {
        return await user.getIdToken(false);
      }
    } catch (e) {
      console.warn("[EVR.GuardUtils] getIdToken failed:", e);
    }

    try {
      if (window.firebase && window.firebase.auth && window.firebase.auth().currentUser) {
        return await window.firebase.auth().currentUser.getIdToken(false);
      }
    } catch (e) {
      console.warn("[EVR.GuardUtils] compat getIdToken failed:", e);
    }

    return null;
  };

  // -----------------------
  //  Prefill helpers
  // -----------------------

  GuardUtils.fetchCheckoutPrefill = async function ({ firebaseIdToken }) {
    if (!firebaseIdToken) return null;

    const endpoint =
      (window.EVR && window.EVR.zeroDegreeCheckoutPrefillEndpoint) ||
      "/apps/eversion-backend/zero-degree-checkout-prefill";

    const res = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${firebaseIdToken}`,
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify({}),
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      console.warn("[EVR.GuardUtils] checkoutPrefill failed:", res.status, txt);
      return null;
    }

    const data = await res.json().catch(() => null);
    if (!data || !data.ok || !data.checkoutPrefill) return null;
    return data.checkoutPrefill;
  };

  GuardUtils.buildCheckoutPrefillUrl = function ({ origin, prefill }) {
    const url = new URL(`${origin}/checkout`);

    const email = prefill?.email ? String(prefill.email).trim() : "";
    const a = prefill?.shipping_address || {};

    if (email) url.searchParams.set("checkout[email]", email);

    if (a.first_name) url.searchParams.set("checkout[shipping_address][first_name]", a.first_name);
    if (a.last_name) url.searchParams.set("checkout[shipping_address][last_name]", a.last_name);
    if (a.address1) url.searchParams.set("checkout[shipping_address][address1]", a.address1);
    if (a.address2) url.searchParams.set("checkout[shipping_address][address2]", a.address2);
    if (a.city) url.searchParams.set("checkout[shipping_address][city]", a.city);
    if (a.zip) url.searchParams.set("checkout[shipping_address][zip]", a.zip);

    if (a.country) url.searchParams.set("checkout[shipping_address][country]", a.country);
    if (a.province_code) url.searchParams.set("checkout[shipping_address][province]", a.province_code);
    else if (a.province) url.searchParams.set("checkout[shipping_address][province]", a.province);

    if (a.phone) url.searchParams.set("checkout[shipping_address][phone]", a.phone);

    return url.toString();
  };

  GuardUtils.redirectToCheckoutWithOptionalCoupon = function ({ checkoutUrl, coupon }) {
    const origin = window.location.origin;
    const target = new URL(checkoutUrl, origin);

    const code = (coupon || "").trim();
    if (code) {
      // Shopify accepts ?discount=CODE on /checkout in many setups
      target.searchParams.set("discount", code);
    }

    window.location.href = target.toString();
    return true;
  };

  // -----------------------
  //  Cart helpers
  // -----------------------

  GuardUtils.fetchCart = async function () {
    const res = await fetch("/cart.js", {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    return await res.json();
  };

  GuardUtils.clearCart = async function () {
    await fetch("/cart/clear.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
    });
  };

  GuardUtils.addToCart = async function ({ variantId, quantity, properties }) {
    const body = { id: variantId, quantity: Number(quantity || 1) || 1 };
    if (properties && typeof properties === "object") body.properties = properties;

    const res = await fetch("/cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(`/cart/add.js failed: ${res.status} ${txt}`);
    }

    return true;
  };

  GuardUtils.replaceCartWithSingleVariant = async function ({ variantId, quantity, properties }) {
    await GuardUtils.clearCart();
    await GuardUtils.addToCart({ variantId, quantity, properties });

    const cart = await GuardUtils.fetchCart().catch(() => null);
    return cart && cart.token ? cart.token : null;
  };

  GuardUtils.mergeCartAttributes = async function (attrs) {
    attrs = attrs || {};
    let cart = null;
    try {
      cart = await GuardUtils.fetchCart();
    } catch (_) {}

    const existing = (cart && cart.attributes) || {};
    const merged = {};

    for (const k in existing) {
      if (Object.prototype.hasOwnProperty.call(existing, k)) merged[k] = existing[k];
    }
    for (const k in attrs) {
      if (Object.prototype.hasOwnProperty.call(attrs, k)) merged[k] = String(attrs[k] ?? "");
    }

    await fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ attributes: merged }),
    });
  };

  // -----------------------
  //  Overlay (shared)
  // -----------------------

  // Use your CDN gif as the “loader + icon” replacement.
  GuardUtils.getOverlayLogoGifUrl = function () {
    return "https://cdn.shopify.com/s/files/1/0989/8980/1803/files/small-logo-darkblue-lightblue-bg-1200-1200.gif?v=1769652778";
  };

  GuardUtils.createThemeOverlay = function (opts) {
    opts = opts || {};
    const initialText = opts.initialText || "Bitte warten …";

    const root = document.createElement("div");
    root.className = "evr-odg";
    root.setAttribute("role", "status");
    root.setAttribute("aria-live", "polite");

    const card = document.createElement("div");
    card.className = "evr-odg__card evr-glass";

    const stack = document.createElement("div");
    stack.className = "evr-odg__stack";

    // NEW: animated gif only (no spinner, no svg icon)
    const logoStage = document.createElement("div");
    logoStage.className = "evr-odg__logostage";

    const img = document.createElement("img");
    img.className = "evr-odg__gif";
    img.src = GuardUtils.getOverlayLogoGifUrl();
    img.alt = "";
    img.decoding = "async";
    img.loading = "eager";
    img.fetchPriority = "high";
    // optional: prevent dragging / selection
    img.draggable = false;

    logoStage.appendChild(img);

    const message = document.createElement("div");
    message.className = "evr-odg__text h3";
    message.textContent = initialText;

    stack.appendChild(logoStage);
    stack.appendChild(message);

    card.appendChild(stack);
    root.appendChild(card);

    GuardUtils.injectOverlayCssOnce();
    document.body.appendChild(root);

    return {
      setText: (t) => (message.textContent = String(t || "")),
      // NOTE: setHtml replaces the whole stack. Keep in mind it will remove the gif unless you include it in your html.
      setHtml: (html) => (stack.innerHTML = html || ""),
      destroy: () => {
        try {
          if (root && root.parentNode) root.parentNode.removeChild(root);
        } catch (_) {}
      },
    };
  };

  GuardUtils.injectOverlayCssOnce = function () {
    if (document.getElementById("evr-odg-style")) return;

    const style = document.createElement("style");
    style.id = "evr-odg-style";
    style.textContent = `
      .evr-odg {
        position: fixed;
        inset: 0;
        z-index: 9999;
        display: grid;
        place-items: center;
        padding: clamp(14px, 3vw, 28px);
        background: rgba(0, 0, 0, 0.5);
      }

      .evr-odg__card {
        background: var(--color-white, #fff);
        width: min(520px, 100%);
        padding: clamp(18px, 3.5vw, 34px);
        border-radius: var(--border-radius, 16px);
        border: 1px solid var(--evr-glass-border, rgba(255,255,255,0.22));
        box-shadow: 0 12px 40px rgba(0,0,0,0.16), 0 1px 0 rgba(255,255,255,0.10) inset;
      }

      .evr-odg__stack{
        display: grid;
        justify-items: center;
        text-align: center;
        gap: clamp(14px, 2.6vw, 18px);
      }

      .evr-odg__logostage{
        width: clamp(96px, 35vw, 260px);
        aspect-ratio: 1 / 1;
        display: grid;
        place-items: center;
      }

      /* Make the GIF responsive, crisp, and square */
      .evr-odg__gif{
        width: 100%;
        height: 100%;
        display: block;
        object-fit: contain;
      }

      .evr-odg__text {
        margin: 0;
        max-width: 46ch;
        font-family: var(--font-body-family, system-ui);
        font-size: clamp(15px, 1.8vw, 17px);
        line-height: var(--font-body-line-height, 1.45);
        color: var(--color-foreground, #111);
        opacity: 0.92;
      }

      .evr-odg__link {
        display: inline-block;
        margin-top: 14px;
        font-family: var(--font-body-family, system-ui);
        font-size: var(--font-body-size, var(--font-paragraph--size, 15px));
        font-weight: 500;
        color: var(--color-foreground-heading, var(--color-foreground, #111));
        text-decoration: underline;
        text-underline-offset: 3px;
      }

      @media (prefers-reduced-motion: reduce) {
        /* we can't stop a GIF, but we can at least reduce other motion (none here) */
      }
    `;
    document.head.appendChild(style);
  };

  // Expose
  window.EVR.GuardUtils = GuardUtils;
})();